import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import * as Sentry from '@sentry/react';
import { api } from '../lib/api';
import { toast } from 'react-toastify';
import Button from '../components/Button';
import GuestAccountPrompt, { GuestAccountHint } from '../components/GuestAccountPrompt';
import { rememberPostLoginDestination } from '../lib/postLoginRedirect';
import { isGuestCheckoutActive, clearGuestCheckoutSession } from '../services/checkout/guestCheckoutSession';
import { formatDateInTimezone, getBrowserTimezone } from '../utils/timezone';

interface Rental {
  _id: string;
  listingId: {
    title: string;
    name: string;
    timezone?: string;
  };
  startDateTime: string;
  endDateTime: string;
  displayEndDateTime: string;
  price: number;
}

interface CheckoutResult {
  success: boolean;
  rentals: Rental[];
  emailStatus?: {
    sent: number;
    failed: number;
    failedRentals: string[];
  };
  alreadyFulfilled?: boolean;
  guestAccountHint?: GuestAccountHint;
}

/**
 * What went wrong, from the buyer's point of view.
 *
 * The distinction that matters is whether their money moved. "Payment failed"
 * next to a card that was charged is the worst thing this page can say.
 */
type FailureKind = 'payment' | 'unavailable' | 'wrong-account' | 'fulfillment';

interface CheckoutFailure {
  kind: FailureKind;
  message: string;
}

const FAILURE_HEADINGS: Record<FailureKind, string> = {
  payment: 'Payment Not Completed',
  unavailable: 'Booking Could Not Be Completed',
  'wrong-account': 'Signed In As A Different Account',
  fulfillment: 'Payment Received, Booking Incomplete',
};

function extractErrorCode(err: unknown): string | undefined {
  const response = (err as { response?: { data?: { errorCode?: string } } })?.response;
  return response?.data?.errorCode;
}

function extractServerMessage(err: unknown): string | undefined {
  const response = (err as { response?: { data?: { error?: string } } })?.response;
  return response?.data?.error;
}

function classifyFailure(err: unknown): CheckoutFailure {
  const code = extractErrorCode(err);
  const serverMessage = extractServerMessage(err);

  switch (code) {
    case 'PAYMENT_FAILED':
      return {
        kind: 'payment',
        message:
          serverMessage ||
          'Your payment did not go through, so nothing was booked and you have not been charged.',
      };
    case 'NOT_AVAILABLE':
      return {
        kind: 'unavailable',
        message:
          serverMessage ||
          'Someone booked one of these slots while your payment was going through. Your payment will be refunded.',
      };
    case 'NOT_AUTHORIZED':
      return {
        kind: 'wrong-account',
        message:
          serverMessage ||
          'This checkout belongs to a different account. Sign in as the account that started it.',
      };
    case 'NOT_FOUND':
      return {
        kind: 'payment',
        message:
          'We could not find this checkout. If you were charged, contact support with your payment receipt.',
      };
    default:
      // Payment succeeded at Stripe; something after it did not. Never tell the
      // buyer their payment failed here.
      return {
        kind: 'fulfillment',
        message:
          serverMessage ||
          'Your payment went through but we could not finish creating your booking. Our team has been notified — please check My Rentals shortly before trying again.',
      };
  }
}

const CheckoutCompletePage: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [result, setResult] = useState<CheckoutResult | null>(null);
  const [failure, setFailure] = useState<CheckoutFailure | null>(null);
  const [hasRemainingCartItems, setHasRemainingCartItems] = useState(false);
  const [cartCheckFailed, setCartCheckFailed] = useState(false);
  const hasCompleted = useRef(false);
  // Captured once on mount rather than read live: a guest who started
  // checkout on CartPage.tsx stays a guest for the rest of this page's
  // lifetime, even after handleCheckoutComplete clears the session below.
  const [isGuest] = useState(isGuestCheckoutActive);

  // Keep the session_id across a login round trip. Cognito returns to a fixed
  // URI and drops the query string, which would otherwise orphan a paid order.
  // Guests never make that round trip — the backend resolves them from the
  // guestCartId cookie instead — so this only applies to a real logged-out user.
  useEffect(() => {
    if (!auth.isAuthenticated && !auth.isLoading && !isGuest) {
      rememberPostLoginDestination(`${location.pathname}${location.search}`);
    }
  }, [auth.isAuthenticated, auth.isLoading, isGuest, location.pathname, location.search]);

  const handleCheckoutComplete = useCallback(async () => {
    try {
      setIsLoading(true);

      const sessionId = searchParams.get('session_id');
      const orderId = searchParams.get('orderId');

      if (!sessionId && !orderId) {
        setFailure({
          kind: 'payment',
          message:
            'This link is missing its payment reference. If you were charged, contact support with your payment receipt.',
        });
        setIsLoading(false);
        return;
      }

      let response: CheckoutResult;

      if (sessionId) {
        // The connected account is resolved server-side from our record of the
        // session, so nothing beyond the session ID is sent
        response = await api.get<CheckoutResult>(
          `/payments/stripe/complete?session_id=${encodeURIComponent(sessionId)}`
        );
      } else if (orderId) {
        // PayPal - note: this might already be handled by PostCheckoutPage
        // This is here for consistency if we redirect here from PayPal too
        response = await api.post<CheckoutResult>('/payments/paypal/capture-order', {
          orderId,
        });
      } else {
        throw new Error('Invalid payment method');
      }

      setResult(response);

      try {
        const cartResponse = await api.get<{ cart: { items: unknown[] } }>('/cart');
        setHasRemainingCartItems(cartResponse.cart?.items?.length > 0);
        setCartCheckFailed(false);
      } catch (cartError) {
        // The booking is confirmed either way, but silently reporting an empty
        // cart could send a buyer away with unpaid items still in it
        Sentry.captureException(cartError);
        setCartCheckFailed(true);
      }

      if (response.alreadyFulfilled) {
        toast.info('This booking was already confirmed.');
      } else {
        toast.success(`Successfully booked ${response.rentals.length} rental(s)!`);
      }

      // Show warning if email failed
      if (response.emailStatus && response.emailStatus.failed > 0) {
        toast.warning(
          `Confirmation email could not be sent for ${response.emailStatus.failed} rental(s)`
        );
      }

      // The order is done; the guest session's only remaining job (letting
      // isGuest gate this page) is captured above, so it is safe to clear.
      if (isGuest) {
        clearGuestCheckoutSession();
      }
    } catch (err) {
      Sentry.captureException(err);
      const classified = classifyFailure(err);
      setFailure(classified);
      toast.error(FAILURE_HEADINGS[classified.kind]);
    } finally {
      setIsLoading(false);
    }
  }, [searchParams, isGuest]); // navigate is stable from useNavigate

  useEffect(() => {
    if ((auth.isAuthenticated || isGuest) && !hasCompleted.current) {
      hasCompleted.current = true;
      handleCheckoutComplete();
    }
  }, [auth.isAuthenticated, isGuest, handleCheckoutComplete]);

  if (!auth.isAuthenticated && !isGuest) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <p className="text-gray-600 mb-2">
          Please log in to finish confirming your booking.
        </p>
        <p className="text-sm text-gray-500 mb-6">
          Your payment is safe. We will pick up where you left off once you are signed in.
        </p>
        <Button onClick={() => auth.signinRedirect()}>Log In</Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Processing your payment...</p>
        </div>
      </div>
    );
  }

  if (failure) {
    const paymentWentThrough = failure.kind === 'unavailable' || failure.kind === 'fulfillment';

    return (
      <div className="max-w-4xl mx-auto py-12">
        <div
          className={`border rounded-lg p-6 ${
            paymentWentThrough
              ? 'bg-yellow-50 border-yellow-200'
              : 'bg-red-50 border-red-200'
          }`}
        >
          <div className="flex items-center mb-4">
            <svg
              className={`w-6 h-6 mr-3 ${
                paymentWentThrough ? 'text-yellow-600' : 'text-red-600'
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2
              className={`text-lg font-semibold ${
                paymentWentThrough ? 'text-yellow-900' : 'text-red-900'
              }`}
            >
              {FAILURE_HEADINGS[failure.kind]}
            </h2>
          </div>
          <p className={`mb-4 ${paymentWentThrough ? 'text-yellow-800' : 'text-red-700'}`}>
            {failure.message}
          </p>
          {paymentWentThrough && (
            <p className="text-sm text-yellow-800 mb-4">
              Do not pay again. Keep this page or your Stripe receipt if you need to
              contact support.
            </p>
          )}
          <div className="flex flex-wrap gap-4">
            {failure.kind === 'wrong-account' ? (
              <Button onClick={() => auth.signinRedirect()}>Switch Account</Button>
            ) : (
              <Button onClick={() => navigate('/my-rentals')}>View My Rentals</Button>
            )}
            <Button onClick={() => navigate('/cart')} variant="secondary">
              Return to Cart
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!result || !result.rentals || result.rentals.length === 0) {
    return (
      <div className="max-w-4xl mx-auto py-12">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <p className="text-yellow-800">No rentals found. Please contact support if you were charged.</p>
          <Button onClick={() => navigate('/')} className="mt-4">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-12">
      {/* Success Banner */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
        <div className="flex items-center">
          <svg
            className="w-8 h-8 text-green-600 mr-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h1 className="text-2xl font-bold text-green-900 mb-1">
              Booking Confirmed!
            </h1>
            <p className="text-green-700">
              {result.rentals.length} rental{result.rentals.length > 1 ? 's' : ''} successfully
              booked
            </p>
          </div>
        </div>
      </div>

      {/* Guest post-purchase CTA */}
      {result.guestAccountHint && (
        <div className="mb-8">
          <GuestAccountPrompt hint={result.guestAccountHint} />
        </div>
      )}

      {/* Email Status Warning */}
      {result.emailStatus && result.emailStatus.failed > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
          <p className="text-yellow-800 text-sm">
            <strong>Note:</strong> Confirmation email could not be sent for{' '}
            {result.emailStatus.failed} rental(s). Your booking is confirmed, but please
            check your My Rentals page for details.
          </p>
        </div>
      )}

      {/* Rental Details */}
      <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Booking Details</h2>

        <div className="space-y-6">
          {result.rentals.map((rental) => (
            <div
              key={rental._id}
              className="border border-gray-200 rounded-lg p-6"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {typeof rental.listingId === 'object'
                      ? rental.listingId.title
                      : 'Rental'}
                  </h3>
                  {typeof rental.listingId === 'object' && rental.listingId.name && (
                    <Link
                      to={`/${rental.listingId.name}`}
                      className="text-sm text-primary-600 hover:text-primary-700"
                    >
                      View Listing
                    </Link>
                  )}
                </div>
                <span className="text-xl font-bold text-gray-900">
                  ${rental.price.toFixed(2)}
                </span>
              </div>

              <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <span className="font-medium text-gray-700">Start:</span>{' '}
                  {formatDateInTimezone(
                    new Date(rental.startDateTime),
                    (typeof rental.listingId === 'object' && rental.listingId.timezone) ||
                      getBrowserTimezone()
                  )}
                </div>
                <div>
                  <span className="font-medium text-gray-700">End:</span>{' '}
                  {formatDateInTimezone(
                    new Date(rental.displayEndDateTime),
                    (typeof rental.listingId === 'object' && rental.listingId.timezone) ||
                      getBrowserTimezone()
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex justify-between items-center text-lg font-bold">
            <span>Total Paid:</span>
            <span className="text-primary-600">
              ${result.rentals.reduce((sum, r) => sum + r.price, 0).toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">What's Next?</h3>
        <ul className="space-y-2 text-blue-800">
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-blue-600 mr-2 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              Confirmation email sent to your registered email address (if delivery succeeded)
            </span>
          </li>
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-blue-600 mr-2 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>Check your reservations page for rental details and QR codes</span>
          </li>
          <li className="flex items-start">
            <svg
              className="w-5 h-5 text-blue-600 mr-2 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>The owner will contact you with any additional details</span>
          </li>
        </ul>
      </div>

      {/* Cart check failure — a silent failure here can hide unpaid items */}
      {cartCheckFailed && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
          <p className="text-yellow-800 text-sm">
            <strong>Note:</strong> We could not check whether anything is left in your
            cart. If you were booking from more than one owner, open your cart to pay for
            the rest.
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        {hasRemainingCartItems || cartCheckFailed ? (
          <>
            <Button
              onClick={() => navigate('/cart')}
              size="lg"
              className="flex-1"
            >
              {hasRemainingCartItems ? 'Return to Cart (items remaining)' : 'Check Your Cart'}
            </Button>
            <Button
              onClick={() => navigate('/my-rentals')}
              variant="secondary"
              size="lg"
              className="flex-1"
            >
              View My Rentals
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => navigate('/my-rentals')}
              size="lg"
              className="flex-1"
            >
              View My Rentals
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="secondary"
              size="lg"
              className="flex-1"
            >
              Browse More Listings
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutCompletePage;

